// Tap to talk on Amazon Transcribe — T57b (12 Sep 2026), agents doc § 7.4: the engine Natanel chose
// for real candidates on T57's measurement (10 Sep 2026). Behind the same `SpeechInput` as the
// browser's own (speech.ts), on the same rules: continuous, ended by two seconds' silence or the
// second tap, never past a minute — and nothing is asked until the recruiter presses Ask.
//
// WHO HEARS THE AUDIO. Amazon Transcribe, streaming, in eu-west-1 on kadmon's own AWS account — the
// provider the worker already uses for voice notes, under the Organization's AI-services opt-out
// (infra/README.md § The AI services opt-out), so the audio is not kept to improve the service —
// and nobody else. The browser sends it straight there: the BFF signs one URL per dictation
// (`GET /dictation`, services/bff/src/speech.ts) and holds no socket, so the brain tier stays
// polling-only (CLAUDE.md § Brain tier). Nothing is recorded anywhere.
//
// THE PIPE. The microphone, through an AudioWorklet handing over a tenth of a second of samples at
// a time → cut to 16-bit PCM at the rate the URL was signed for (`createDownsampler`) → one
// AudioEvent per chunk (eventstream.ts) → the socket. What comes back is TranscriptEvents: results
// that firm up as more is heard, each under an id, which `transcriptLog` keeps in order — a later
// version of a result replacing the earlier. An empty AudioEvent ends the stream; Transcribe sends
// its last results and closes, and the close ends the dictation.
//
// WHAT IS TESTED WHERE. The engine is built from three parts it is handed — the URL, the
// microphone, the socket — so transcribe.test.ts drives it with fakes: every timing rule, the
// audio kept while the socket opens, the end frame, Transcribe's refusals. The framing is
// eventstream.test.ts's. The two browser adapters at the foot of this file are the rest, and
// `pnpm console:speech` proves them: it plays the measurement's list to this same code in Chrome.
import type { DictationResponse } from '@vera/bff';
import { audioEvent, decodeMessage } from './eventstream.ts';
import {
  END_GRACE_MS,
  MAX_LISTEN_MS,
  NOTHING_HEARD_MS,
  SILENCE_MS,
  type AudioTrackLike,
  type Listening,
  type SpeechInput,
} from './speech.ts';
import { silenceWatcher, type FinishedAsk } from './silence.ts';

/**
 * Said under the composer while the mic is open (app/voice.tsx): who hears what is said. "Our",
 * not the firm's name: that line is set in capitals, and kadmon is drawn in lower case everywhere
 * (T64) — the phone check's drawn-text check caught the name in capitals here on 12 Sep 2026.
 */
export const TRANSCRIBE_HEARS = 'Amazon Transcribe hears this, on our own AWS account';

// --- what comes back -------------------------------------------------------------------------

/** One result of a TranscriptEvent, as far as this engine reads it. */
export interface TranscriptResult {
  ResultId?: string;
  IsPartial?: boolean;
  Alternatives?: { Transcript?: string }[];
}

export type TranscribeMessage =
  | { kind: 'transcript'; results: TranscriptResult[] }
  | { kind: 'exception'; type: string; message: string }
  | { kind: 'other' };

/** One frame from Transcribe: some results, its refusal, or something this engine does not read. */
export function readTranscribeMessage(bytes: Uint8Array): TranscribeMessage {
  const { headers, payload } = decodeMessage(bytes);
  const body = new TextDecoder().decode(payload);
  if (headers[':message-type'] === 'exception') {
    let message = body;
    try {
      message = (JSON.parse(body) as { Message?: string }).Message ?? body;
    } catch {
      // A refusal in plain text is still its own message.
    }
    return {
      kind: 'exception',
      type: String(headers[':exception-type'] ?? 'UnknownException'),
      message,
    };
  }
  if (headers[':message-type'] === 'event' && headers[':event-type'] === 'TranscriptEvent') {
    const parsed = JSON.parse(body) as { Transcript?: { Results?: TranscriptResult[] } };
    return { kind: 'transcript', results: parsed.Transcript?.Results ?? [] };
  }
  return { kind: 'other' };
}

/**
 * Everything heard so far, in order. A result arrives partial and is sent again, under the same
 * id, as more is heard, until its final version; so a result's latest words replace its earlier
 * ones, and a new id is a new stretch of speech after the last.
 */
export function transcriptLog() {
  const order: string[] = [];
  const words = new Map<string, string>();
  const text = () =>
    order
      .map((id) => words.get(id) ?? '')
      .filter((part) => part !== '')
      .join(' ');
  return {
    /** Takes one event's results, and says whether what was heard changed. */
    take(results: readonly TranscriptResult[]): boolean {
      const before = text();
      for (const result of results) {
        const id = result.ResultId;
        if (!id) continue;
        if (!words.has(id)) order.push(id);
        words.set(id, (result.Alternatives?.[0]?.Transcript ?? '').replace(/\s+/g, ' ').trim());
      }
      return text() !== before;
    },
    text,
  };
}

/** Transcribe's refusal as a reason `explainSpeechError` has a sentence for. */
export function refusalCode(exceptionType: string): string {
  return exceptionType === 'LimitExceededException' ? 'transcribe-busy' : 'transcribe-failed';
}

// --- what goes out ---------------------------------------------------------------------------

/**
 * The microphone's samples at the rate the URL was signed for, as 16-bit little-endian PCM
 * (`media-encoding=pcm`). Down only, each output sample the average of its share of the input:
 * 48 kHz is three samples to one, 44.1 kHz two or three by turns. A share carries across chunks,
 * so where the worklet cut the audio makes no difference to what is sent.
 */
export function createDownsampler(fromRate: number, toRate: number) {
  if (!(fromRate >= toRate)) {
    throw new Error(`the microphone runs at ${fromRate} Hz, below the ${toRate} Hz asked for`);
  }
  const step = fromRate / toRate;
  let sum = 0;
  let count = 0;
  let taken = 0;
  let next = step;
  return {
    push(samples: Float32Array): Uint8Array {
      const out: number[] = [];
      for (const sample of samples) {
        sum += sample;
        count += 1;
        taken += 1;
        if (taken >= next) {
          out.push(sum / count);
          sum = 0;
          count = 0;
          next += step;
        }
      }
      const pcm = new Uint8Array(out.length * 2);
      const view = new DataView(pcm.buffer);
      out.forEach((value, i) => {
        const clamped = Math.max(-1, Math.min(1, value));
        view.setInt16(i * 2, Math.round(clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff), true);
      });
      return pcm;
    },
  };
}

// --- the engine ------------------------------------------------------------------------------

/** The microphone, opened: samples flow once `started` resolves, with the rate they come at. */
export interface Capture {
  readonly started: Promise<number>;
  stop(): void;
}

/**
 * Opens the microphone — or, for the measurement, a clip's track — calling back with raw samples.
 * Called inside the tap and synchronously, so an adapter can start audio where Safari allows it.
 */
export type OpenCapture = (
  onSamples: (samples: Float32Array) => void,
  source?: AudioTrackLike,
) => Capture;

export interface SocketHandlers {
  open(): void;
  message(bytes: Uint8Array): void;
  /** The socket is gone: closed by Transcribe after its last result, refused, or dropped. */
  close(): void;
}

export interface Socket {
  send(bytes: Uint8Array): void;
  /** Closes it from this side. No handler is called after. */
  close(): void;
}

export type OpenSocket = (url: string, on: SocketHandlers) => Socket;

export interface TranscribeParts {
  /** One signed URL per dictation — the BFF's `GET /dictation`, or the measurement's own server. */
  session: () => Promise<DictationResponse>;
  capture: OpenCapture;
  socket: OpenSocket;
  /** T451c: a model's read of whether the words so far are finished (silence.ts); absent, the fixed silence alone. */
  finished?: FinishedAsk;
}

/** Why the microphone would not open, as `explainSpeechError` names it. */
export function captureErrorCode(error: unknown): string {
  const name = (error as { name?: unknown } | null)?.name;
  return name === 'NotAllowedError' || name === 'SecurityError' ? 'not-allowed' : 'audio-capture';
}

export function transcribeSpeechInput(parts: TranscribeParts): SpeechInput {
  return {
    whoHears: TRANSCRIBE_HEARS,
    listen(handlers, options = {}) {
      let text = '';
      let error: string | null = null;
      let over = false;
      let stopping = false;
      let ended = false;
      let quiet: ReturnType<typeof setTimeout> | null = null;
      let cap: ReturnType<typeof setTimeout> | null = null;
      let capture: Capture | null = null;
      let socket: Socket | null = null;
      let open = false;
      let downsample: ((samples: Float32Array) => Uint8Array) | null = null;
      // What the microphone heard before the socket was open, sent the moment it is.
      const waiting: Float32Array[] = [];
      const log = transcriptLog();

      const detach = () => {
        over = true;
        watcher.clear();
        if (quiet !== null) clearTimeout(quiet);
        if (cap !== null) clearTimeout(cap);
        capture?.stop();
        socket?.close();
      };
      const finish = () => {
        if (over) return;
        detach();
        handlers.onEnd(text === '' ? { error: error ?? 'no-speech' } : { text });
      };
      const fail = (code: string) => {
        error ??= code;
        finish();
      };
      // T451c: the fixed silence, or a model's read of the words when the app gave one (silence.ts).
      const watcher = silenceWatcher({
        silenceMs: options.silenceMs ?? SILENCE_MS,
        mode: options.mode ?? 'dictation',
        finished: parts.finished,
        stop: () => stop(),
      });
      const wait = (ms: number, then: () => void) => {
        if (quiet !== null) clearTimeout(quiet);
        quiet = setTimeout(then, ms);
      };
      const send = (samples: Float32Array) => {
        if (!open || socket === null || downsample === null) {
          waiting.push(samples);
          return;
        }
        const pcm = downsample(samples);
        if (pcm.length > 0) socket.send(audioEvent(pcm));
      };
      // The empty AudioEvent: Transcribe sends what it still holds, then closes.
      const end = () => {
        if (ended || !open || socket === null) return;
        ended = true;
        socket.send(audioEvent(new Uint8Array(0)));
      };
      const stop = () => {
        if (over || stopping) return;
        stopping = true;
        watcher.clear();
        capture?.stop();
        end();
        // Transcribe owes its last words and a close. If they never come, end for it.
        wait(END_GRACE_MS, finish);
      };
      const session: Listening = {
        stop,
        abort() {
          if (over) return;
          detach();
        },
      };

      wait(NOTHING_HEARD_MS, stop);
      cap = setTimeout(stop, MAX_LISTEN_MS);

      try {
        capture = parts.capture((samples) => {
          if (!over && !stopping) send(samples);
        }, options.source);
      } catch (thrown) {
        fail(captureErrorCode(thrown));
        return session;
      }
      const opened = capture;
      opened.started.catch((thrown: unknown) => fail(captureErrorCode(thrown)));

      void (async () => {
        let signed: DictationResponse;
        try {
          signed = await parts.session();
        } catch {
          fail('transcribe-unavailable');
          return;
        }
        let rate: number;
        try {
          rate = await opened.started;
        } catch {
          return; // said by the catch above
        }
        if (over) return;
        try {
          downsample = createDownsampler(rate, signed.sampleRate).push;
        } catch {
          fail('audio-capture');
          return;
        }
        socket = parts.socket(signed.url, {
          open() {
            if (over) return;
            open = true;
            for (const samples of waiting.splice(0)) send(samples);
            if (stopping) end();
          },
          message(bytes) {
            if (over) return;
            let read: TranscribeMessage;
            try {
              read = readTranscribeMessage(bytes);
            } catch {
              fail('transcribe-failed');
              return;
            }
            if (read.kind === 'exception') {
              fail(refusalCode(read.type));
              return;
            }
            if (read.kind !== 'transcript' || !log.take(read.results)) return;
            text = log.text();
            handlers.onText(text);
            if (!stopping) {
              if (quiet !== null) clearTimeout(quiet);
              watcher.heard(text);
            }
          },
          close() {
            if (over) return;
            // Closed before it ever opened: the URL was refused, or nothing answered.
            if (!open) error ??= 'transcribe-failed';
            finish();
          },
        });
      })();

      return session;
    },
  };
}

// --- the browser's half ----------------------------------------------------------------------

/**
 * The AudioWorklet that taps the microphone: a tenth of a second of its first channel at a time,
 * posted to the page. `sampleRate` is the worklet scope's own global.
 */
const TAP = `
class KadmonTap extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = new Float32Array(Math.round(sampleRate / 10));
    this.filled = 0;
  }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel) {
      let at = 0;
      while (at < channel.length) {
        const take = Math.min(channel.length - at, this.buffer.length - this.filled);
        this.buffer.set(channel.subarray(at, at + take), this.filled);
        this.filled += take;
        at += take;
        if (this.filled === this.buffer.length) {
          this.port.postMessage(this.buffer.slice());
          this.filled = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor('kadmon-tap', KadmonTap);
`;

/**
 * The microphone through Web Audio, or null where the browser lacks a part of it. The worklet is
 * loaded from a `blob:` URL: the brain's Content-Security-Policy (infra/src/console-csp.ts, report-only
 * since T411) allows `blob:` for scripts, and enforcing it must keep doing so, or the mic stops.
 */
export function webAudioCapture(scope: object): OpenCapture | null {
  const {
    AudioContext: Context,
    AudioWorkletNode: WorkletNode,
    MediaStream: Stream,
    Blob: BlobOf,
    URL: Url,
    navigator: nav,
  } = scope as {
    AudioContext?: typeof AudioContext;
    AudioWorkletNode?: typeof AudioWorkletNode;
    MediaStream?: typeof MediaStream;
    Blob?: typeof Blob;
    URL?: typeof URL;
    navigator?: Navigator;
  };
  const mediaDevices = nav?.mediaDevices;
  if (!Context || !WorkletNode || !Stream || !BlobOf || !Url || !mediaDevices?.getUserMedia) {
    return null;
  }

  return (onSamples, source) => {
    // Made here, inside the tap and before anything is awaited: Safari lets an AudioContext start
    // only in the gesture that asked for it. Not awaited: a context that never starts hears
    // nothing, and the silence rule ends the dictation with "Nothing was heard".
    const context = new Context();
    void context.resume();
    let stream: MediaStream | null = null;
    let own = false;
    let stopped = false;
    const nodes: AudioNode[] = [];
    const teardown = () => {
      for (const node of nodes) node.disconnect();
      if (own) for (const track of stream?.getTracks() ?? []) track.stop();
      if (context.state !== 'closed') void context.close();
    };
    const started = (async () => {
      if (source) {
        stream = new Stream([source as MediaStreamTrack]);
      } else {
        stream = await mediaDevices.getUserMedia({
          audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
        });
        own = true;
      }
      if (stopped) throw new DOMException('the dictation ended first', 'AbortError');
      const url = Url.createObjectURL(new BlobOf([TAP], { type: 'text/javascript' }));
      try {
        await context.audioWorklet.addModule(url);
      } finally {
        Url.revokeObjectURL(url);
      }
      if (stopped) throw new DOMException('the dictation ended first', 'AbortError');
      const input = context.createMediaStreamSource(stream);
      const tap = new WorkletNode(context, 'kadmon-tap');
      tap.port.onmessage = (event: MessageEvent<Float32Array>) => {
        if (!stopped) onSamples(event.data);
      };
      input.connect(tap);
      // Onward, silently: a node that leads nowhere is not run by every browser.
      tap.connect(context.destination);
      nodes.push(input, tap);
      return context.sampleRate;
    })();
    started.catch(teardown);
    return {
      started,
      stop() {
        if (stopped) return;
        stopped = true;
        teardown();
      },
    };
  };
}

/** The browser's WebSocket, or null where there is none. Binary frames only: Transcribe sends no text. */
export function browserSocket(scope: object): OpenSocket | null {
  const { WebSocket: Ws } = scope as { WebSocket?: typeof WebSocket };
  if (typeof Ws !== 'function') return null;
  return (url, on) => {
    const socket = new Ws(url);
    socket.binaryType = 'arraybuffer';
    socket.onopen = () => on.open();
    socket.onmessage = (event: MessageEvent<unknown>) => {
      if (event.data instanceof ArrayBuffer) on.message(new Uint8Array(event.data));
    };
    // A refused handshake or a drop fires `error` and then `close`; the close ends the dictation.
    socket.onclose = () => on.close();
    return {
      send: (bytes) => socket.send(bytes),
      close() {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        if (socket.readyState === Ws.CONNECTING || socket.readyState === Ws.OPEN) socket.close();
      },
    };
  };
}
